import { Trans } from "@lingui/react/macro";
import { Button } from "@rakazo/ui-web";
import { useEffect, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { PageLoading } from "../components/PageLoading";
import { rpc } from "../lib/rpc";

export const SHOPIFY_CONNECTED_PATH = "/integrations/shopify/connected";
const INTEGRATIONS = "/app?integrations";
const PENDING_KEY = "rakazo.shopifyInstall";
const COMPLETE_ATTEMPTS = 10;
const COMPLETE_INTERVAL_MS = 1500;

/** A claim from a Shopify install outlives sign-up, which leaves this page for onboarding. */
function stashClaim(token: string) {
  try {
    localStorage.setItem(PENDING_KEY, token);
  } catch {
    // Without storage the merchant can still connect from Integrations.
  }
}

function takeClaim(): string | null {
  try {
    const token = localStorage.getItem(PENDING_KEY);
    localStorage.removeItem(PENDING_KEY);
    return token;
  } catch {
    return null;
  }
}

/** Where a signed-in merchant goes next when a Shopify install is still waiting to be claimed. */
export function pendingShopifyClaimPath(): string | null {
  try {
    return localStorage.getItem(PENDING_KEY) ? SHOPIFY_CONNECTED_PATH : null;
  } catch {
    return null;
  }
}

/** Signed out: keep the claim and sign in first. */
export function ShopifyConnectedSignedOut() {
  const [params] = useSearchParams();
  const { pathname } = useLocation();
  const token = params.get("token");
  if (token) stashClaim(token);
  const next = token ? pathname : `${pathname}?${params.toString()}`;
  return <Navigate to={`/sign-in?next=${encodeURIComponent(next)}`} replace />;
}

/**
 * The end of a Shopify install: claim the store for this company and run its sign-in, then wait
 * for the connection and open Integrations.
 */
export function ShopifyConnectedPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [failed, setFailed] = useState(params.has("failed"));
  const handled = useRef(false);
  useEffect(() => {
    if (handled.current || failed) return;
    handled.current = true;
    // Taken in every case, so a stale claim never sends the merchant back here later.
    const stored = takeClaim();
    const connectionId = params.get("connection");
    if (connectionId) {
      void (async () => {
        for (let attempt = 0; attempt < COMPLETE_ATTEMPTS; attempt += 1) {
          const row = await rpc.connections.complete({ connectionId }).catch(() => null);
          if (row?.status === "connected") return navigate(INTEGRATIONS, { replace: true });
          if (!row || row.status === "revoked" || row.status === "error") break;
          await new Promise((resolve) => setTimeout(resolve, COMPLETE_INTERVAL_MS));
        }
        setFailed(true);
      })();
      return;
    }
    const token = params.get("token") ?? stored;
    if (!token) {
      navigate(INTEGRATIONS, { replace: true });
      return;
    }
    rpc.connections.claimShopifyInstall({ token }).then(
      ({ authorizationUrl }) => {
        if (authorizationUrl) window.location.assign(authorizationUrl);
        else navigate(INTEGRATIONS, { replace: true });
      },
      () => setFailed(true),
    );
  }, [failed, navigate, params]);
  if (!failed) return <PageLoading />;
  return (
    <div className="grid min-h-screen place-items-center bg-background p-6 text-center">
      <div>
        <div className="text-lg text-foreground">
          <Trans>Could not connect Shopify</Trans>
        </div>
        <Button type="button" className="mt-5" onClick={() => navigate(INTEGRATIONS)}>
          <Trans>Open Integrations</Trans>
        </Button>
      </div>
    </div>
  );
}
